import { Box, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { DefaultTheme } from '@mui/styles/defaultTheme';
import { useHistory } from 'react-router-dom';
import type { PxUnderwriterPageState } from '../reducer/types';
import Numeral from 'numeral';
import { toUniversalUtcDate } from '../../../../utils/date.utils';
import { getCurrencySymbolByProduct } from '../../../../i18n/utils';
import { useCaptureOrigin } from '../../../../components/hooks/navigation/navigationOrigin';
import type { ProductType } from '../../../../types';

const UnderwriterRelatedQuotes = (
  props: PxUnderwriterPageState & {
    /**
     * Rounded and filled with no underline, like the Selects beside it in the
     * underwriter globals row. The legacy header keeps the standard look.
     */
    readonly filled?: boolean;
  }
) => {
  const classes = useSelectClasses();
  const history = useHistory();
  const captureOrigin = useCaptureOrigin();

  const handleQuoteChange = (e: SelectChangeEvent<string>) => {
    const selectedQuote = props.quotesList.find(
      ({ quoteNumber }) => quoteNumber === e.target.value
    );

    return history.push(
      `/admin/inbox/${selectedQuote?.id}/${selectedQuote?.product}/underwrite`,
      captureOrigin()
    );
  };

  return (
    <Select
      variant="standard"
      disableUnderline={props.filled}
      classes={classes}
      onChange={handleQuoteChange}
      defaultValue={props.quoteDetails?.quote?.quoteNumber}
      sx={{
        backgroundColor: (theme: DefaultTheme) =>
          theme.palette.background.active,
        width: '17rem',
        // The theme's shape radius is a fixed px, like the Selects it matches.
        ...(props.filled && { borderRadius: '5px', overflow: 'hidden' }),
      }}
      renderValue={(selectedValue) => selectedValue}
    >
      {props.quotesList.map((quote) => {
        return (
          <MenuItem
            key={quote.id}
            value={quote.quoteNumber}
            sx={
              !quote.raterXCompatible
                ? {
                    pointerEvents: 'none',
                    opacity: 0.5,
                  }
                : {}
            }
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                {quote.quoteNumber}
                <Box
                  style={{
                    width: '13.5rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {quote.agencyName}
                </Box>
              </Box>
              <Box textAlign="right">
                {quote.agencyStatus.split('_').join(' ')}
                {quote.aggLimit != null && (
                  <Box fontSize="0.85rem">
                    Limit:{' '}
                    {`${getCurrencySymbolByProduct(
                      quote.product as ProductType
                    )}${Numeral(quote.aggLimit)
                      .format('0.[0]a')
                      .toUpperCase()}`}
                  </Box>
                )}
                <Box fontSize="0.85rem">
                  Expiry: {toUniversalUtcDate(quote.expiresOn)}
                </Box>
              </Box>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default UnderwriterRelatedQuotes;

const useSelectClasses = makeStyles<DefaultTheme>(({ palette }) => ({
  select: {
    border: 'none',
    minWidth: '5.5rem',
    outline: 'none',

    borderRadius: 5,
    '&:focus': {
      backgroundColor: palette.background.active,
    },
  },
}));
